import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Loader2, Save, Trash2, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';

const CATEGORIES = [
  "Fryzjer", "Dentysta", "Mechanik samochodowy", "Kosmetyczka", "Hydraulik", "Elektryk",
  "Prawnik", "Psycholog", "Weterynarz", "Fizjoterapeuta", "Restauracja", "Dietetyk",
  "Fotograf", "Trener personalny", "Groomer", "Budowlaniec", "Firma sprzątająca",
  "Księgowa", "Tatuażysta", "Serwis komputerowy", "Salon masażu", "Kwiaciarnia",
  "Ogrodnik", "Szkoła jazdy", "Architekt", "Notariusz", "Nieruchomości", "Ubezpieczenia",
  "Fotowoltaika", "Klimatyzacja", "Taxi", "Przeprowadzki", "Wulkanizacja", "Pomoc drogowa",
  "Szkoła językowa", "Siłownia", "Cukiernia", "Sala weselna", "DJ i wodzirej",
  "Drukarnia", "Agencja reklamowa", "Medycyna estetyczna", "Stolarz", "Wypożyczalnia aut",
  "Brukarz", "Okna i drzwi", "Ogrodzenia", "Inna",
];

export default function EditBusinessPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    axios.get(`${API}/public/business-edit/${token}`).then(res => {
      const s = res.data.submission;
      setData(s);
      setForm({
        name: s.name || '',
        category: s.category || '',
        city: s.city || '',
        address: s.address || '',
        phone: s.phone || '',
        email: s.email || '',
        website: s.website || '',
        facebook: s.facebook || '',
        instagram: s.instagram || '',
        description: s.description || '',
        price_range: s.price_range || '',
        opening_hours: s.opening_hours || '',
      });
    }).catch(err => {
      setError(err.response?.data?.detail || 'Nie znaleziono wpisu');
    }).finally(() => setLoading(false));
  }, [token]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/public/business-edit/${token}`, form);
      toast.success('Zmiany zapisane!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Na pewno usunąć firmę z katalogu? Tej operacji nie można cofnąć.')) return;
    try {
      await axios.delete(`${API}/public/business-edit/${token}`);
      setDeleted(true);
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-6">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Nie znaleziono wpisu</h1>
        <p className="text-gray-400 mb-6">{error}</p>
        <Link to="/dodaj-firme"><Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-6">Dodaj nową firmę</Button></Link>
      </div>
    </div>
  );

  if (deleted) return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-6">
      <div className="text-center">
        <Check className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Firma usunięta</h1>
        <p className="text-gray-400 mb-6">Wpis został usunięty z katalogu.</p>
        <Link to="/"><Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-6">Strona główna</Button></Link>
      </div>
    </div>
  );

  return (
    <>
      <Helmet><title>Edytuj firmę | ApisAI</title></Helmet>
      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
            <Link to="/katalog/uslugi" className="text-gray-400 hover:text-white text-sm">Katalog</Link>
          </div>
        </nav>

        <div className="pt-28 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2" data-testid="edit-biz-title">Edytuj firmę</h1>
                <p className="text-gray-400 text-sm">{data?.name} — {data?.city}</p>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${data?.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {data?.status === 'active' ? 'Aktywna' : 'Oczekuje na weryfikację'}
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 mb-1.5 block">Nazwa firmy</label>
                  <input value={form.name} onChange={e => update('name', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50" data-testid="edit-biz-name" />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-400 mb-1.5 block">Kategoria</label>
                  <select value={form.category} onChange={e => update('category', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-500/50 text-sm" data-testid="edit-biz-category">
                    <option value="" className="bg-[#0B0F1A]">Wybierz kategorię...</option>
                    {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#0B0F1A]">{c}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Miasto</label>
                    <input value={form.city} onChange={e => update('city', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Adres</label>
                    <input value={form.address} onChange={e => update('address', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Telefon</label>
                    <input value={form.phone} onChange={e => update('phone', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Email</label>
                    <input value={form.email} onChange={e => update('email', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-400 mb-1.5 block">Opis</label>
                  <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50 resize-none" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <input value={form.website} onChange={e => update('website', e.target.value)} placeholder="Strona www"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50 placeholder-gray-500" />
                  <input value={form.facebook} onChange={e => update('facebook', e.target.value)} placeholder="Facebook"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50 placeholder-gray-500" />
                  <input value={form.instagram} onChange={e => update('instagram', e.target.value)} placeholder="Instagram"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50 placeholder-gray-500" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input value={form.price_range} onChange={e => update('price_range', e.target.value)} placeholder="Zakres cen"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50 placeholder-gray-500" />
                  <input value={form.opening_hours} onChange={e => update('opening_hours', e.target.value)} placeholder="Godziny otwarcia"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50 placeholder-gray-500" />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-xl" data-testid="edit-biz-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Zapisz zmiany
                  </Button>
                  <Button onClick={handleDelete} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl" data-testid="edit-biz-delete">
                    <Trash2 className="w-4 h-4 mr-2" /> Usuń
                  </Button>
                </div>

                <p className="text-center text-gray-500 text-xs mt-4">
                  Zapisz ten link — to Twój klucz do edycji wpisu.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
